import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Navigate } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { ANY_SPORT_SCOUTING_CENTER_WATCHLISTS } from '@sorare/core/src/constants/__generated__/routes';
import { idFromObject } from '@sorare/core/src/gql/idFromObject';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { WatchlistEmptyState } from 'components/watchlists/WatchlistEmptyState';
import { WatchlistHeader } from 'components/watchlists/WatchlistHeader';

import type {
  WatchlistPageQuery,
  WatchlistPageQueryVariables,
} from './__generated__/index.graphql';

const WATCHLIST_PAGE_QUERY = gql`
  query WatchlistPageQuery(
    $id: ID!
    $sport: Sport!
    $withCollectionPredictions: Boolean = true
  ) {
    so5 {
      so5Fixture(type: UPCOMING, eventType: CLASSIC, sport: $sport) {
        slug
      }
    }
    market {
      id
      watchlist(id: $id) {
        slug
        id
        totalPlayersCount
        ...WatchlistHeader_watchlist
      }
    }
  }
  ${WatchlistHeader.fragments.watchlist}
` as TypedDocumentNode<WatchlistPageQuery, WatchlistPageQueryVariables>;

type WatchlistSearchComponentProps = {
  watchlistId: string;
  so5FixtureSlug?: string;
};

type Props = {
  id: string;
  sport: Sport;
  WatchlistSearchComponent: React.ComponentType<WatchlistSearchComponentProps>;
};

export const WatchlistPage = ({
  id,
  sport,
  WatchlistSearchComponent,
}: Props) => {
  const { data } = useQuery(WATCHLIST_PAGE_QUERY, {
    variables: {
      id: idFromObject(id),
      sport,
    },
    skip: !id,
  });

  if (!data) return null;

  const {
    market: { watchlist },
    so5: { so5Fixture },
  } = data;

  const so5FixtureSlug = so5Fixture?.slug;

  if (!watchlist)
    return (
      <Navigate
        to={generateSportPath(ANY_SPORT_SCOUTING_CENTER_WATCHLISTS, {
          sport,
        })}
      />
    );

  return (
    <>
      <PageHeader
        text={watchlist.title}
        backTo={generateSportPath(ANY_SPORT_SCOUTING_CENTER_WATCHLISTS, {
          sport,
        })}
        disableBackBrowser
      />
      <WatchlistHeader watchlist={watchlist} />
      {watchlist.totalPlayersCount === 0 ? (
        <WatchlistEmptyState />
      ) : (
        <WatchlistSearchComponent
          watchlistId={watchlist.id}
          so5FixtureSlug={so5FixtureSlug}
        />
      )}
    </>
  );
};
